/* jshint esversion: 6 */

import {Assert} from "common/Assert";
import {LoggingService} from "logging/LoggingService";

function makeIndexedDBAvailable() {

	if( !window.indexedDB ) {
		window.indexedDB = window.webkitIndexedDB || window.mozIndexedDB;
	}

	if( !window.IDBKeyRange ) {
		window.IDBKeyRange = window.webkitIDBKeyRange || window.mozIDBKeyRange;
	}

	return !!window.indexedDB;
}

function IndexedDBPlayerStore() {

	var indexedDBAvailable = makeIndexedDBAvailable(),
		dbId = "ttpsPlayer",
		dataStoreId = "userData",
		db = null,
		version = null;

	function init( dbVersion ) {

		version = dbVersion || 1;

		if( !indexedDBAvailable ){
			return createFailedPromise( false );
		}

		return open();
	}

	function open() {

		return new Promise( (resolve) => {

			const request = window.indexedDB.open(dbId, version);

			//The database has changed its version (For IE 10 and Firefox)
			request.onupgradeneeded = function (event) {
				db = event.target.result;
				updateUserData();
			};

			request.onsuccess = (event) => {

				db = event.target.result;

				//The database has changed its version (For Chrome)
				if (version !== db.version && window.webkitIndexedDB) {
					const setVersionRequest = db.setVersion(version);
					setVersionRequest.onsuccess = updateUserData;
				}

				resolve(true);
			};

			request.onerror = function (event) {
				LoggingService.error(`Database error: ${event}`);
				resolve(false);
			};
		});
	}

	function updateUserData() {

		//Delete previous object store
		if (db.objectStoreNames.contains( dataStoreId )) {
			db.deleteObjectStore( dataStoreId );
		}

		db.createObjectStore( dataStoreId );
	}

	function readUserData( uuid ) {

		Assert.checkArgument( uuid );

		if( !db ){
			return createFailedPromise("");
		}

		return new Promise( (resolve) => {

			const trans = db.transaction([dataStoreId], "readonly"),
				store = trans.objectStore(dataStoreId),
				userDataRequest = store.get(uuid);

			userDataRequest.onsuccess = function () {
				resolve(userDataRequest.result || "");
			};
		});
	}

	function each( callback ){

		Assert.checkArgument( callback );

		return new Promise( (resolve) => {

			const trans = db.transaction([dataStoreId], "readonly"),
				store = trans.objectStore(dataStoreId),
				keyRange = window.IDBKeyRange.lowerBound(0),
				cursorRequest = store.openCursor(keyRange);

			cursorRequest.onsuccess = function (event) {
				const cursor = event.target.result;
				if (cursor === false || cursor === null) {
					return;
				}

				if (callback(cursor.key, cursor.value) !== "break") {
					cursor.continue();
				}
			};

			trans.oncomplete = function () {
				resolve();
			};
		});
	}

	function put( uuid, userData ) {

		Assert.checkArgument( uuid );

		if( !db ){
			return createFailedPromise( false );
		}

		return new Promise( (resolve) => {

			const trans = db.transaction([dataStoreId], "readwrite"),
				store = trans.objectStore(dataStoreId),
				request = store.put( userData, uuid );

			request.onsuccess = function () {
				resolve(true);
			};

			request.onerror = function () {
				resolve(false);
			};
		});
	}

	function deleteUserData( uuid ) {

		Assert.checkArgument( uuid );

		if( !db ){
			return createFailedPromise( false );
		}

		return new Promise( (resolve) => {

			const transaction = db.transaction([dataStoreId], "readwrite"),
				store = transaction.objectStore(dataStoreId),
				request = store.delete(uuid);

			request.onsuccess = function () {
				resolve(true);
			};

			request.onerror = function (event) {
				LoggingService.error(`Error deleting: ${event}`);
				resolve(false);
			};
		});
	}

	function clear() {

		if( !db ){
			return createFailedPromise( false );
		}

		return new Promise( (resolve) => {

			const transaction = db.transaction([dataStoreId], "readwrite"),
				store = transaction.objectStore(dataStoreId),
				request = store.clear();

			request.onsuccess = function () {
				resolve(true);
			};

			request.onerror = function (event) {
				LoggingService.error(`Error while clearing: ${event}`);
				resolve(false);
			};
		});
	}

	function createFailedPromise( result ){
		return new Promise( (resolve) => {
			resolve( result );
		});
	}

	// revealing public api
	return {
		init: init,
		put: put,
		get: readUserData,
		each: each,
		delete: deleteUserData,
		close: function() {
			if( db ){
				db.close();
				db = null;
			}
		},
		clear : clear
	};
}

export default IndexedDBPlayerStore;
